import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { Search } from '@shared/components/ui/Search';
import { FiltersDropdown } from '@shared/components/ui/FiltersDropdown';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReturnedFiltersPanel } from './ReturnedFiltersPanel';
import { cn } from '@shared/utils/utils';

interface FiltersSectionProps {
  handleSearch: (value: string) => void;
  onFilterChange?: (conditions: FilterConditionOutput[]) => void;
  className?: string;
}

export const FiltersSection = ({ handleSearch, onFilterChange, className }: FiltersSectionProps) => {
  const { t } = useTranslation('returnedModule');
  const { t: tUI } = useTranslation('ui');

  // 🎯 Hook useQueryParams para sincronizar con URL
  const { params, updateParam } = useQueryParams({
    search: { type: 'string', default: '' },
    filters: { type: 'json', default: [] as FilterConditionOutput[] },
  });

  // 🎯 Estado local sincronizado con URL
  const [inputValue, setInputValue] = useState(params.search ?? '');
  const filtersFromUrl = useMemo(() => (Array.isArray(params.filters) ? params.filters : []), [params.filters]);

  // 🎯 Efecto para sincronizar con cambios en URL
  useEffect(() => {
    const searchFromUrl = params.search ?? '';
    setInputValue((prev) => (prev !== searchFromUrl ? searchFromUrl : prev));

    handleSearch(searchFromUrl);
    onFilterChange?.(filtersFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.search, filtersFromUrl]);

  // 🎯 Memoizar handler para evitar bucle infinito
  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      updateParam('filters', conditions);
      onFilterChange?.(conditions);
    },
    [updateParam, onFilterChange],
  );

  // 🎯 Handler para el cambio del input - Solo afecta al estado local
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  // 🎯 Handler para ejecutar búsqueda - Solo comunica al padre cuando necesario
  const handleSearchSubmit = useCallback(() => {
    const trimmedValue = inputValue.trim();
    updateParam('search', trimmedValue);
    handleSearch(trimmedValue);
  }, [inputValue, updateParam, handleSearch]);

  const activeFiltersCount = filtersFromUrl.length;

  return (
    <div className={cn('flex flex-wrap items-center gap-3', className)}>
      <Search
        placeholder={t('search.placeholder')}
        value={inputValue}
        onChange={handleInputChange}
        onEnter={handleSearchSubmit}
        dataTestId="search"
        inputDataTestId="search-input"
      />

      <FiltersDropdown
        title={tUI('advancedFilter.filters', 'Filtros')}
        activeFiltersCount={activeFiltersCount}
        dropdownContentClassName="sm:min-w-[350px] sm:max-w-[400px] p-4 bg-body-bg text-foreground border border-card-border"
      >
        <ReturnedFiltersPanel initialConditions={filtersFromUrl} onChange={handleFilterChange} />
      </FiltersDropdown>
    </div>
  );
};
